import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price: number) {
  return price.toLocaleString("en-NG", { style: "currency", currency: "NGN" });
}

export function formatNoOfReviews(noOfReviews: number) {
  const isFiniteNumber = Number.isFinite(noOfReviews);
  if (!isFiniteNumber) return "0";

  const sign = noOfReviews < 0 ? "-" : "";
  const absolute = Math.abs(noOfReviews);

  if (absolute < 1000) {
    return sign + Math.round(absolute).toString();
  }

  const units = [
    { value: 1_000_000_000_000, suffix: "T" },
    { value: 1_000_000_000, suffix: "B" },
    { value: 1_000_000, suffix: "M" },
    { value: 1_000, suffix: "k" },
  ];

  for (let i = 0; i < units.length; i++) {
    const unit = units[i];
    if (absolute >= unit.value) {
      let scaled = absolute / unit.value;

      // Round to one decimal place
      let rounded = Number(scaled.toFixed(1));

      // Handle rollover like 999.9k -> 1.0M
      if (rounded >= 1000 && i > 0) {
        const next = units[i - 1];
        scaled = absolute / next.value;
        rounded = Number(scaled.toFixed(1));
        const text = rounded.toFixed(1);
        return sign + (text.endsWith(".0") ? text.slice(0, -2) : text) + next.suffix;
      }

      const text = rounded.toFixed(1);
      return sign + (text.endsWith(".0") ? text.slice(0, -2) : text) + unit.suffix;
    }
  }

  return sign + Math.round(absolute).toString();
}

export function truncateText(text: string, maxLength: number = 126): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

export function formatCompactNumber(num: number): string {
  if (num < 1000) return num.toString();
  if (num < 1_000_000) return (num / 1_000).toFixed(num % 1000 === 0 ? 0 : 1) + "k";
  if (num < 1_000_000_000) return (num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 2) + "M";
  return (num / 1_000_000_000).toFixed(num % 1_000_000_000 === 0 ? 0 : 2) + "B";
}

export function formatCurrency(amount: number, compact = false): string {
  if (compact) {
    return "₦" + formatCompactNumber(amount);
  }
  return amount.toLocaleString("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export const maskEmail = (email: string) => {
  const [localPart, domain] = email.split("@");
  if (localPart.length <= 2) return email;
  const masked = localPart[0] + "***" + localPart[localPart.length - 1];
  return `${masked}@${domain}`;
};

export const maskPhone = (phone: string) => {
  if (phone.length <= 6) return phone;
  return phone.slice(0, 4) + "***" + phone.slice(-3);
};

export const getIdentifierType = (identifier: string) => {
  // Email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Phone regex pattern (11 digits, may start with 0)
  const phoneRegex = /^0?\d{10}$/;

  if (emailRegex.test(identifier)) return "email";
  if (phoneRegex.test(identifier)) return "phone";
  return "ippis";
};
